import React, { useState, useEffect } from 'react';
import { Car, MapPin, Clock, ArrowRight, Navigation, X } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';

const TaxiPendienteWidget = () => {
    const [solicitud, setSolicitud] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dismissed, setDismissed] = useState(false);
    const navigate = useNavigate();

    // Check dismiss state
    useEffect(() => {
        const today = new Date().toDateString();
        const key = `widget_dismiss_taxi_${today}`;
        if (localStorage.getItem(key)) setDismissed(true);
    }, []);

    useEffect(() => {
        fetchMiSolicitud();
    }, []);

    const fetchMiSolicitud = async () => {
        try {
            const response = await axiosInstance.get('/api/taxi-requests/mis-activas');
            if (response.data.success && response.data.solicitud) {
                setSolicitud(response.data.solicitud);
            }
        } catch (error) {
            // Si no hay endpoint aún, verificar localStorage o estado simulado
            console.log('Taxi requests API not available yet');
        } finally {
            setLoading(false);
        }
    };

    const handleDismiss = () => {
        if (window.confirm('¿Deseas ocultar este widget por hoy?')) {
            const today = new Date().toDateString();
            localStorage.setItem(`widget_dismiss_taxi_${today}`, 'true');
            setDismissed(true);
        }
    };

    if (loading || !solicitud || dismissed) return null;

    const estadoConfig = {
        pending_approval: {
            label: 'Esperando aprobación',
            bg: 'from-orange-500 to-amber-600',
            bgLight: 'bg-orange-50',
            text: 'text-orange-600',
            border: 'border-orange-200',
            icon: '⏳'
        },
        pending: {
            label: 'Pendiente',
            bg: 'from-amber-500 to-orange-600',
            bgLight: 'bg-amber-50',
            text: 'text-amber-600',
            border: 'border-amber-200',
            icon: '⏳'
        },
        confirmed: {
            label: 'Confirmado',
            bg: 'from-blue-500 to-indigo-600',
            bgLight: 'bg-blue-50',
            text: 'text-blue-600',
            border: 'border-blue-200',
            icon: '✓'
        }
    };

    const config = estadoConfig[solicitud.status] || estadoConfig.pending;

    return (
        <section className={`relative rounded-2xl overflow-hidden border ${config.border} shadow-sm group`}>
            {/* Dismiss button */}
            <button
                onClick={handleDismiss}
                className="absolute top-3 right-3 z-20 w-6 h-6 rounded-full bg-white/80 backdrop-blur-sm text-gray-400 hover:text-gray-600 hover:bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-sm"
                title="Ocultar por hoy"
            >
                <X size={14} />
            </button>
            {/* Gradient header */}
            <div className={`bg-gradient-to-r ${config.bg} px-5 py-4 text-white`}>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <Car size={20} />
                    </div>
                    <div>
                        <h3 className="font-bold text-lg">Solicitud de Taxi</h3>
                        <p className="text-white/80 text-sm flex items-center gap-1">
                            {config.icon} {config.label}
                        </p>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className={`${config.bgLight} p-5`}>
                {/* Ruta */}
                <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm mb-4">
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-emerald-500" />
                            <span className="text-gray-900 font-medium truncate">{solicitud.origin}</span>
                        </div>
                        <div className="ml-1.5 border-l-2 border-dashed border-gray-200 h-4" />
                        <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-blue-500" />
                            <span className="text-gray-900 font-medium truncate">{solicitud.destination}</span>
                        </div>
                    </div>
                </div>

                {/* Fecha y hora */}
                <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-lg ${config.bgLight} ${config.text} flex items-center justify-center`}>
                        <Clock size={18} />
                    </div>
                    <div className="flex-1">
                        <p className="text-xs text-gray-500">Fecha y hora</p>
                        <p className="font-bold text-gray-900">
                            {new Date(solicitud.date + 'T00:00:00').toLocaleDateString('es-VE', { day: 'numeric', month: 'short' })} a las {solicitud.time}
                        </p>
                    </div>
                </div>

                <button
                    onClick={() => navigate('/solicitud-taxi?tab=history')}
                    className="w-full py-3 rounded-xl bg-gray-900 text-white font-medium flex items-center justify-center gap-2 hover:bg-gray-800 transition-colors"
                >
                    Ver solicitud
                    <ArrowRight size={16} />
                </button>
            </div>
        </section>
    );
};

export default TaxiPendienteWidget;
